import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkles, Loader2, CheckCircle2, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Public paywall dialog shown to share-preview viewers when they try to
 * download a PDF. Captures email/name/company and POSTs to
 * /api/share/{share_id}/trial-signup, which records a sales lead and
 * returns trial info.
 *
 * Props: open, onOpenChange, shareId, resourceName
 */
export function TrialPaywallDialog({ open, onOpenChange, shareId, resourceName, brand = {} }) {
  const appName = brand.app_name || 'INZARA';
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const reset = (v) => {
    if (!v) { setEmail(''); setName(''); setCompany(''); setDone(false); setError(null); }
    onOpenChange(v);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!email || !email.includes('@')) { setError('Please enter a valid work email.'); return; }
    if (!shareId) { setError('Share link missing — please refresh.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/share/${encodeURIComponent(shareId)}/trial-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, company }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `Could not start trial (HTTP ${res.status})`);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="trial-paywall-dialog">
        <div className="flex items-center justify-center gap-2 pt-1" data-testid="paywall-brand">
          {brand.logo_url
            ? <img src={brand.logo_url} alt={appName} className="h-7 object-contain" />
            : <span className="text-base font-heading font-bold" style={brand.primary_color ? { color: brand.primary_color } : undefined}>{appName}</span>}
        </div>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Lock className="w-4 h-4 text-primary" />
            {done ? 'You\'re in!' : 'Unlock the full dashboard'}
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="space-y-3 mt-2 text-center" data-testid="trial-paywall-success">
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 w-fit mx-auto">
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
            </div>
            <p className="text-sm text-foreground">Thanks — your 14-day trial is reserved.</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              We've emailed <span className="text-foreground/80 font-medium">{email}</span> with a sign-in link.
              Once you're signed in you'll be able to download the PDF, edit, and share this dashboard with your team.
            </p>
            <Button onClick={() => reset(false)} className="w-full rounded-lg" data-testid="trial-paywall-close-btn">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-1">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Downloading <span className="text-foreground/80 font-medium">{resourceName || 'this dashboard'}</span> as PDF
              is reserved for {appName} users. Start a free <span className="text-primary font-semibold">14-day trial</span> —
              no credit card — and get the full dashboard, plus your own.
            </p>
            <div>
              <Label className="text-xs text-muted-foreground">Work email *</Label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="rounded-lg bg-muted/50 border-border/50 mt-1"
                required
                data-testid="trial-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Your name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 mt-1" data-testid="trial-name-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Company</Label>
                <Input value={company} onChange={e => setCompany(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 mt-1" data-testid="trial-company-input" />
              </div>
            </div>
            {error && (
              <p className="text-[11px] text-rose-400" data-testid="trial-paywall-error">{error}</p>
            )}
            <Button type="submit" disabled={submitting} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="trial-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Start 14-day trial
            </Button>
            <p className="text-[10px] text-muted-foreground/70 text-center leading-relaxed">
              By continuing you agree to receive product emails. No credit card required.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
